import type { Lifecycle } from "../shared/lifecycle";
import type { ApproveOutcome, GeneratedView, PublishOutcome } from "./outcomes";
import type { ClientView, ConnectionView, DeliveryView, MasterView, PlatformView, SatelliteView, SiteView, TemplateView } from "./model";

// The shared shape the Client Detail sections work from. `PageData` is what was loaded (validated); `ViewState` is what
// the person has done on this page and must survive every repaint (open accordions, ticked boxes, typed topic, results
// of actions, which buttons are busy). Sections are pure functions of (data, state) plus callbacks in `Actions`.

export interface SectionFailure {
  message: string;
}

export interface PageData {
  client: ClientView;
  warnings: string[];
  sites: SiteView[];
  connections: ConnectionView[];
  platforms: PlatformView[];
  /** Secondary loads: null with a failure recorded means "could not be read", which is different from "there are none". */
  templates: TemplateView[] | null;
  satellites: SatelliteView[] | null;
  masters: MasterView[] | null;
  failures: { templates?: SectionFailure; satellites?: SectionFailure; masters?: SectionFailure };
}

export type Notice = { tone: "ok" | "warn" | "bad" | "info"; text: string; detail?: string[]; link?: { text: string; href: string } };

export interface DeliveryEntry {
  state: "loading" | "ready" | "error";
  /** Read in the background so the Publish button can be honest; not shown until the person opens Delivery. */
  quiet?: boolean;
  message?: string;
  editorial?: string | null;
  delivery?: DeliveryView | null;
}

export interface ViewState {
  /** Keys of actions currently running. A button whose key is here ignores clicks and shows a busy state. */
  pending: Set<string>;
  deployNotice: Notice | null;
  templateNotice: Notice | null;
  satelliteOpen: Set<string>;
  /** Ticked platforms per satellite id. Absent = not touched yet, so the default (already-deployed platforms) applies. */
  satelliteChecked: Map<string, Set<string>>;
  satelliteNotices: Map<string, Notice[]>;
  newSatelliteService: string;
  satelliteCreateNotice: Notice | null;
  topic: string;
  generateNotice: Notice | null;
  generated: GeneratedView | null;
  /** One line under the preview title, kept in step with what has happened to the article since (approve, publish). */
  generatedLine: string | null;
  /** Articles that automatic mode already approved AND submitted in this visit: an ordinary first Publish must not be offered. */
  autoAccepted: Set<string>;
  /** Result of the last Approve on the generated preview. */
  generatedApproval: ApproveOutcome | null;
  generatedPublish: PublishOutcome | null;
  /** Per article: what the last approve/publish said. Kept until the next action on that article. */
  approvals: Map<string, ApproveOutcome>;
  publishes: Map<string, PublishOutcome>;
  delivery: Map<string, DeliveryEntry>;
  deliveryOpen: Set<string>;
}

export function newViewState(): ViewState {
  return {
    pending: new Set(),
    deployNotice: null,
    templateNotice: null,
    satelliteOpen: new Set(),
    satelliteChecked: new Map(),
    satelliteNotices: new Map(),
    newSatelliteService: "",
    satelliteCreateNotice: null,
    topic: "",
    generateNotice: null,
    generated: null,
    generatedLine: null,
    autoAccepted: new Set(),
    generatedApproval: null,
    generatedPublish: null,
    approvals: new Map(),
    publishes: new Map(),
    delivery: new Map(),
    deliveryOpen: new Set(),
  };
}

export interface Actions {
  deploy(platform: string): Promise<void>;
  saveTemplate(pageType: string, templateId: string): Promise<void>;
  deploySatellite(satelliteId: string, platforms: string[]): Promise<void>;
  createSatellite(serviceKey: string): Promise<void>;
  generate(topic: string): Promise<void>;
  approve(masterId: string, from: "list" | "preview"): Promise<void>;
  /** `mode: "new"` is a deliberate new publication (a new Idempotency-Key); the default reuses the key of an unresolved attempt. */
  publish(masterId: string, from: "list" | "preview", mode?: "default" | "new"): Promise<void>;
  toggleDelivery(masterId: string): Promise<void>;
  refreshDelivery(masterId: string): Promise<void>;
  reloadSection(section: "templates" | "satellites" | "masters"): Promise<void>;
  repaint(...sections: SectionName[]): void;
}

export type SectionName = "deploy" | "facts" | "templates" | "sites" | "satellites" | "connections" | "content";

export interface Ctx {
  clientId: string;
  data: PageData;
  state: ViewState;
  actions: Actions;
  life: Lifecycle;
}
